import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { useI18n } from "../lib/i18n";
import { AlertTriangle, ShieldCheck, Sparkles } from "lucide-react";
import { Button } from "../components/ui/button";
import { MatchCard } from "./MatchesPage";

export default function AdminAlertsPage() {
  const { t } = useI18n();
  const [matches, setMatches] = useState([]);
  const [reviews, setReviews] = useState({});
  const [loading, setLoading] = useState(true);
  const [hideExcluded, setHideExcluded] = useState(false);
  const [notifs, setNotifs] = useState([]);

  const keyOf = (m) => `${m.ante_mortem.id}::${m.post_mortem.id}`;

  useEffect(() => {
    api.get("/admin/high-matches")
      .then((r) => {
        setMatches(r.data);
        const map = {};
        r.data.forEach((m) => { map[keyOf(m)] = m.review; });
        setReviews(map);
      })
      .finally(() => setLoading(false));
    api.get("/admin/notifications", { params: { unread_only: true } })
      .then((r) => setNotifs(r.data.items || []))
      .catch(() => {});
  }, []);

  const markAllRead = async () => {
    try {
      await api.post("/admin/notifications/mark-read");
      setNotifs([]);
    } catch {
      // noop
    }
  };

  const visible = matches.filter((m) => {
    const status = reviews[keyOf(m)]?.status || "new";
    return !(hideExcluded && status === "excluded");
  });

  return (
    <div className="mx-auto max-w-6xl px-6 py-10" data-testid="admin-alerts-page">
      <p className="font-body text-xs uppercase tracking-[0.18em] text-[#6B7F82]">Admin · alerts</p>
      <div className="mt-1 flex items-center gap-3">
        <span className="grid h-9 w-9 place-items-center rounded-md bg-[#9E4747] text-white"><AlertTriangle size={18} /></span>
        <h1 className="font-heading text-3xl font-semibold text-[#1A2024]">Priority matches (≥85%)</h1>
      </div>
      <p className="mt-2 max-w-2xl font-body text-sm text-[#5C6873]">
        High-confidence pairings between a missing-person report and an unidentified body. These still require
        human review before any identification — a high score means many fields align, not a confirmed match.
      </p>

      <div className="mt-5">
        <label className="flex w-fit cursor-pointer items-center gap-2 font-body text-sm text-[#5C6873]">
          <input
            type="checkbox"
            checked={hideExcluded}
            onChange={(e) => setHideExcluded(e.target.checked)}
            data-testid="alerts-hide-excluded"
          />
          {t("review.hide_excluded")}
        </label>
      </div>

      {notifs.length > 0 && (
        <div className="mt-6 rounded-lg border border-[#C99B4B]/40 bg-[#C99B4B]/10 p-5" data-testid="circ-notifications">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-center gap-2">
              <Sparkles size={16} className="text-[#8a6a2e]" />
              <h2 className="font-heading text-base font-semibold text-[#1A2024]">
                New circumstance-match notifications ({notifs.length})
              </h2>
            </div>
            <Button variant="outline" size="sm" className="border-[#C99B4B]/40 bg-white text-[#8a6a2e]" onClick={markAllRead} data-testid="notif-mark-read-btn">
              Mark all as read
            </Button>
          </div>
          <div className="mt-3 space-y-2">
            {notifs.map((n) => (
              <div key={n.id} className="flex flex-wrap items-center justify-between gap-2 rounded-md border border-[#C99B4B]/30 bg-white p-3" data-testid={`notif-${n.id}`}>
                <div className="min-w-0 font-body text-sm text-[#1A2024]">
                  <Link to={`/ante-mortem/${n.ante_id}`} className="font-semibold hover:underline" data-testid={`notif-ante-${n.id}`}>
                    {n.ante_name || n.ante_ref || "Missing person"}
                  </Link>
                  <span className="mx-1.5 text-[#6B7F82]">↔</span>
                  <Link to={`/post-mortem/${n.post_id}`} className="font-mono font-semibold hover:underline" data-testid={`notif-post-${n.id}`}>
                    {n.post_ref || "Unidentified case"}
                  </Link>
                  <span className="ml-2 rounded-full bg-[#4A7C59]/10 px-2 py-0.5 text-[11px] font-medium text-[#4A7C59]">{n.score}/100</span>
                  {n.circumstance_hits?.length > 0 && (
                    <span className="ml-2 text-[12px] text-[#8a6a2e]">{n.circumstance_hits.join(", ")}</span>
                  )}
                </div>
                <span className="font-body text-xs text-[#6B7F82]">{n.created_at ? new Date(n.created_at).toLocaleString() : ""}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="mt-8 space-y-4">
        {loading && <div className="font-body text-sm text-[#5C6873]" data-testid="alerts-loading">Loading alerts…</div>}

        {!loading && visible.length === 0 && (
          <div className="rounded-lg border border-dashed border-[#E2E8F0] bg-[#F8F9FA] p-10 text-center" data-testid="alerts-empty">
            <ShieldCheck className="mx-auto text-[#4A7C59]" />
            <h3 className="mt-2 font-heading text-lg font-semibold text-[#1A2024]">No priority alerts</h3>
            <p className="mx-auto mt-1 max-w-md font-body text-sm text-[#5C6873]">
              There are currently no matches at or above 85%. New high-confidence pairings will appear here automatically.
            </p>
          </div>
        )}

        {visible.map((m, i) => (
          <MatchCard
            key={`${keyOf(m)}-${i}`}
            m={m}
            review={reviews[keyOf(m)]}
            onReviewChange={(data) => setReviews((prev) => ({ ...prev, [keyOf(m)]: data }))}
            priority
            footer={!m.ante_mortem.is_person_file && (
              <div className="mt-4 flex flex-wrap gap-3">
                <Link to={`/ante-mortem/${m.ante_mortem.id}`} className="font-body text-sm font-medium text-[#2E3A46] hover:underline" data-testid={`alert-view-ante-${m.ante_mortem.id}`}>
                  View missing-person report →
                </Link>
                <Link to={`/post-mortem/${m.post_mortem.id}`} className="font-body text-sm font-medium text-[#2E3A46] hover:underline" data-testid={`alert-view-post-${m.post_mortem.id}`}>
                  View unidentified case →
                </Link>
              </div>
            )}
          />
        ))}
      </div>
    </div>
  );
}
